import React from 'react';

function Completion({ formData, packageType }) {
  const software = formData.software?.subscription || 'Xero';
  const isOneOff = packageType === 'Start-up Pack' || packageType === 'Clean Slate';
  const isStarter = packageType === 'Starter';
  const isBASOnly = packageType === 'BAS Only';
  const gstNotApplicable = formData.compliance?.gst_method === 'Not Applicable';
  // For Start-up Pack: no Xero setup instructions (client doesn't have software yet)
  const showXeroSetup = !isOneOff;
  // BAS Agent nomination only when BAS work is in scope
  const showBASAgentSteps = !isOneOff && !isStarter && !gstNotApplicable;

  return (
    <div className="card fade-in" style={{ maxWidth: '700px', margin: '0 auto', textAlign: 'center' }}>
      <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>✅</div>
      
      <h1 style={{ marginBottom: '1rem', color: 'var(--success-green)' }}>Submission Complete!</h1>
      
      <p style={{ fontSize: '1.125rem', color: 'var(--text-secondary)', marginBottom: '2rem' }}>
        Thank you, <strong>{formData.contacts?.name}</strong>! Your onboarding submission has been received.
      </p>

      <div className="alert alert-info" style={{ textAlign: 'left', marginBottom: '2rem' }}>
        <strong>📋 What Happens Next:</strong>
        <p style={{ margin: '0.5rem 0 0 0' }}>
          ST Books will review your submitted information and verify your identity documents within 1-2 business days. 
          Once verified, you'll receive a confirmation email with your welcome pack and next steps.
        </p>
      </div>

      <div className="alert alert-success" style={{ textAlign: 'left', marginBottom: '2rem' }}>
        <strong>✓ What's Been Completed:</strong>
        <ul style={{ margin: '0.5rem 0 0 1.5rem', paddingLeft: '0' }}>
          <li>Your business information has been recorded</li>
          <li>Identity verification documents uploaded</li>
          <li>Client Agreement signed and recorded</li>
          <li>BAS Agent Authority submitted to ST Books</li>
          <li>Your client record has been created</li>
        </ul>
      </div>

      <hr style={{ margin: '2rem 0', border: 'none', borderTop: '1px solid var(--bg-gray-200)' }} />

      <div style={{ textAlign: 'left', marginBottom: '2rem' }}>
        <h3 style={{ marginBottom: '1rem' }}>Next Steps</h3>
        
        <div style={{ display: 'grid', gap: '1rem' }}>
          <div style={{
            backgroundColor: 'var(--bg-gray-50)',
            padding: '1.5rem',
            borderRadius: '0.375rem',
            borderLeft: '4px solid var(--accent-teal)'
          }}>
            <strong style={{ display: 'block', marginBottom: '0.5rem' }}>1. Check Your Email</strong>
            <p style={{ margin: 0, fontSize: '0.875rem' }}>
              You'll receive an email at <strong>{formData.contacts?.email}</strong> with:
            </p>
            <ul style={{ margin: '0.5rem 0 0 1.5rem', fontSize: '0.875rem', paddingLeft: '0' }}>
              <li>Copy of your signed Client Agreement</li>
              {showXeroSetup && <li>Instructions for {software} setup</li>}
              {showBASAgentSteps && <li>Guide to nominate ST Books as your BAS Agent in ATO Online Services</li>}
            </ul>
          </div>

          {showXeroSetup && (
          <div style={{
            backgroundColor: 'var(--bg-gray-50)',
            padding: '1.5rem',
            borderRadius: '0.375rem',
            borderLeft: '4px solid var(--accent-teal)'
          }}>
            <strong style={{ display: 'block', marginBottom: '0.5rem' }}>2. Add ST Books to Your {software}</strong>
            <p style={{ margin: 0, fontSize: '0.875rem' }}>
              Follow the instructions in your email to grant ST Books access to your {software} account. 
              This allows us to manage your bookkeeping efficiently.
            </p>
          </div>
          )}

          {showBASAgentSteps && (
          <div style={{
            backgroundColor: 'var(--bg-gray-50)',
            padding: '1.5rem',
            borderRadius: '0.375rem',
            borderLeft: '4px solid var(--accent-teal)'
          }}>
            <strong style={{ display: 'block', marginBottom: '0.5rem' }}>3. Nominate BAS Agent (ATO Online Services)</strong>
            <p style={{ margin: 0, fontSize: '0.875rem' }}>
              Log in to ATO Online Services for Business and nominate ST Books as your BAS Agent. 
              You'll need our BAS Agent Number: <strong data-testid="completion-bas-agent-number">26378315</strong>
            </p>
          </div>
          )}

          <div style={{
            backgroundColor: 'var(--bg-gray-50)',
            padding: '1.5rem',
            borderRadius: '0.375rem',
            borderLeft: '4px solid var(--accent-teal)'
          }}>
            <strong style={{ display: 'block', marginBottom: '0.5rem' }}>{showXeroSetup || showBASAgentSteps ? '4. ' : '2. '}ST Books Will Contact You</strong>
            <p style={{ margin: 0, fontSize: '0.875rem' }}>
              Sarah will reach out within 1-2 business days to:
            </p>
            <ul style={{ margin: '0.5rem 0 0 1.5rem', fontSize: '0.875rem', paddingLeft: '0' }}>
              <li>Verify your identity documents</li>
              <li>Schedule your initial setup meeting</li>
              <li>Discuss any specific requirements</li>
              <li>Set up your bookkeeping schedule</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="alert alert-info" style={{ textAlign: 'left' }}>
        <strong>📞 Need Help?</strong>
        <p style={{ margin: '0.5rem 0 0 0' }}>
          If you have any questions or need assistance, please contact ST Books:
        </p>
        <ul style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.5rem' }}>
          <li><strong>Email:</strong> info@stbooks.com.au</li>
          <li><strong>Phone:</strong> 0416 419 763</li>
          <li><strong>Address:</strong> 2/10a Greenwood Avenue, Narraweena, NSW 2099</li>
        </ul>
      </div>

      <div style={{ marginTop: '3rem', padding: '2rem', backgroundColor: 'var(--primary-navy)', borderRadius: '0.375rem', color: 'white' }}>
        <h3 style={{ color: 'white', marginBottom: '0.5rem' }}>Welcome to ST Books!</h3>
        <p style={{ margin: 0, color: 'white', opacity: 0.9 }}>
          We're excited to partner with you and support your business success.
        </p>
      </div>

      <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginTop: '2rem' }}>
        You can now close this window.
      </p>
    </div>
  );
}

export default Completion;